import React, { useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import { CssBaseline } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import classNames from 'classnames'
import { PuiMomentUtils } from '@pbt/pbt-ui-components'

import { clientForApolloFlow } from '~/api/graphql/graphqlCore'
import { AGENT_CHAT_PANE_WIDTH_PX } from '~/constants/agentChatLayout'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'
import useMaintenanceMode from '~/hooks/useMaintenanceMode'
import ConditionalAuth0Provider from '~/providers/ConditionalAuth0Provider'
import { auth0Enabled } from '~/utils'
import { useChangeLanguage } from '~/utils/useChangeLanguage'

import { ChatMode, useAgentChatState } from './agent/AgentChatContext'
import AgentChatManager from './agent/AgentChatManager'
import Auth from './auth/Auth'
import LogoutComponent from './auth/LogoutComponent'
import SignInComponent from './auth/SignInComponent'
import MonitoringWidget from './dashboard/MonitoringWidget'
import RootPage from './dashboard/RootPage'
import StorefrontAnalyticsWidget from './dashboard/StorefrontAnalyticsWidget'
import AuthWatcher from './dashboard/watchers/AuthWatcher'
import EasterEggWatcher from './dashboard/watchers/EasterEggWatcher/EasterEggWatcher'
import MonitorWatcher from './dashboard/watchers/MonitorWatcher'
import NotificationWatcher from './dashboard/watchers/NotificationWatcher'
import PageTitleWatcher from './dashboard/watchers/PageTitleWatcher'
import DialogsManager from './dialogs-manager/DialogsManager'
import ErrorWatcher from './error/ErrorWatcher'
import LastGateErrorsBoundary from './error/LastGateErrorsBoundary'
import Favicon from './Favicon'
import GlobalHistory from './GlobalHistory'
import Registration from './registration/Registration'
import AtomicComponentsPage from './sandbox/AtomicComponentsPage'
import ScrollToTop from './ScrollToTop'
import TermsAndConditionsPage from './terms-and-conditions/TermsAndConditionsPage'
import WindowVariableInjector from './WindowVariableInjector'

const useStyles = makeStyles(
  (theme) => ({
    page: {
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    agentChatOpen: {
      '& main': {
        marginRight: AGENT_CHAT_PANE_WIDTH_PX,
      },
      '& main #right-rail-slide': {
        right: AGENT_CHAT_PANE_WIDTH_PX,
      },
    },
  }),
  { name: 'App' },
)

const App = () => {
  const classes = useStyles()
  useMaintenanceMode()

  const [passwordExpirationError, setPasswordExpirationError] = useState(false)

  const { i18n } = useChangeLanguage()
  const { isChatOpen, chatMode } = useAgentChatState()
  const isAgentChatEnabled = useIsAgentChatEnabled()

  return (
    <ApolloProvider client={clientForApolloFlow}>
      <BrowserRouter>
        <ConditionalAuth0Provider>
          <I18nextProvider i18n={i18n}>
            <LastGateErrorsBoundary>
              <Favicon />
              <LocalizationProvider dateAdapter={PuiMomentUtils}>
                <CssBaseline />
                <WindowVariableInjector />
                <MonitoringWidget />
                <StorefrontAnalyticsWidget />
                <GlobalHistory />
                <ErrorWatcher />
                <NotificationWatcher />
                <PageTitleWatcher />
                <MonitorWatcher />
                <ScrollToTop>
                  <>
                    <DialogsManager />
                    <AgentChatManager />
                    <EasterEggWatcher />
                    <div
                      className={classNames(classes.page, {
                        [classes.agentChatOpen]:
                          isChatOpen &&
                          isAgentChatEnabled &&
                          chatMode === ChatMode.RAIL,
                      })}
                    >
                      <Routes>
                        {auth0Enabled ? (
                          <>
                            <Route
                              element={<LogoutComponent />}
                              path="auth/logout"
                            />
                            <Route
                              element={<SignInComponent />}
                              path="auth/signin"
                            />
                          </>
                        ) : (
                          <>
                            <Route element={<Auth />} path="auth/*" />
                          </>
                        )}
                        <Route element={<Registration />} path="register/*" />
                        <Route
                          element={<TermsAndConditionsPage />}
                          path="terms-and-conditions"
                        />
                        <Route
                          element={<AtomicComponentsPage />}
                          path="sandbox/atomic"
                        />
                        {!passwordExpirationError && (
                          <Route element={<RootPage />} path="/*" />
                        )}
                      </Routes>
                    </div>
                    <AuthWatcher
                      setPasswordExpirationError={setPasswordExpirationError}
                    />
                  </>
                </ScrollToTop>
              </LocalizationProvider>
            </LastGateErrorsBoundary>
          </I18nextProvider>
        </ConditionalAuth0Provider>
      </BrowserRouter>
    </ApolloProvider>
  )
}

export default App
